import { CommonDialogs } from "@mcleod/common";
import { ContextMenuLabelProps, DataSource, DataSourceAction, DataSourceExecutionEvent, DataSourceMode, Image, Label, Layout, Panel, SaveButton, SelectionEvent, Snackbar, Table, TableRow, TableRowDisplayEvent, TableRowMoveEvent, Tabset, Toast } from "@mcleod/components";
import { Api, CompanySettings, DisplayType, ModelRow, StringUtil } from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import { AutogenLayoutPartialShipmentManifestDetails } from "./autogen/AutogenLayoutPartialShipmentManifestDetails";
import { YSplit } from "./YSplit";

const STOP_DEPARTED = "D";
const AVAILABLE_STATUS_CODE = "A";
const SPLIT_PICKUP = "SP";
const SPLIT_DELIVERY = "SD";
const PICKUP = "PU";
const DELIVERY = "SO";

interface OrderStopSequence {
    pickupIndex?: number;
    deliveryIndex?: number;
}

export class PartialShipmentManifestDetails extends AutogenLayoutPartialShipmentManifestDetails {
    public static readonly PICKUP_STOP_TYPES: string[] = [PICKUP, SPLIT_PICKUP, "AP"];
    public static readonly DELIVERY_STOP_TYPES: string[] = [DELIVERY, SPLIT_DELIVERY, "AD"];
    public static readonly MANIFEST_CREATION_LAYOUT = "lme/dispatch/PartialShipmentManifestCreationSlideout";

    private tableStopsSaveButton: SaveButton;
    private _manifestId: string = "";
    private _movementId: string = "";
    public onStopDetailsChange?: (hasUnsavedChanges: boolean) => void;
    public get movementId(): string { return this._movementId; }
    public set movementId(value: string) { this._movementId = value; }
    public get manifestId(): string { return this._manifestId; }
    public set manifestId(value: string) { this._manifestId = value; }

    override onLoad(): void {
        const tableStops = this.tableStopTables;
        const dataSource = this.sourcePartialShipmentManifestStopDetails;
        const saveStopsButton = SaveButton.create({
            caption: "Save Stops", id: "stopsSaveButton",
            onClick: () => {this.saveStopChanges(tableStops, dataSource)}
        });
        saveStopsButton.enabled = false;
        this.tabsetManifestDetails.afterTools = [this.getManifestTotals(), saveStopsButton];
        this.tableStopsSaveButton = saveStopsButton;
    }


    /** This is an event handler for the onRowDisplay event of tableStopTables. */
    tableStopTablesOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow: TableRow = event.target;
        const modelRow: ModelRow = tableRow.data as ModelRow;
        const labelStop: Label = tableRow.findComponentById("labelStopStatus") as Label;
        const labelStopType: Label = tableRow.findComponentById("labelStopType") as Label;
        this.setupApptRequiredConfirmed(tableRow);
        const stopStatus = modelRow?.get("stop.status");
        const stopType = modelRow?.get("stop.stop_type");
        const schedArriveEarly = modelRow?.get("stop.actual_arrival", null);
        const schedArriveLate = modelRow?.get("stop.actual_departure", null);
        this.setLabelStopStatus(stopStatus, labelStop, schedArriveEarly, schedArriveLate);
        if (modelRow?.get("stop.order_id") != null) {
            tableRow.contextMenuItems = () => this.createContextMenuItems(tableRow);
        }
        if (SPLIT_PICKUP == stopType) {
            labelStopType.text = "Split pickup";
        }
        if (STOP_DEPARTED === stopStatus) {
            tableRow.draggable = false;
            tableRow.hideDragHandle();
        }
        const isFirstStopSplitPickup = tableRow.index === 0 && stopType === SPLIT_PICKUP;
        if (isFirstStopSplitPickup) {
            tableRow.draggable = false;
            tableRow.hideDragHandle();
        }
    }

    public setupApptRequiredConfirmed(tableRow: TableRow) {
        const modelRow = tableRow.data as ModelRow;
        const apptBadgeRequired = tableRow.findComponentById("imageAppointmentRequired") as Image;
        const apptBadgeConfirmed = tableRow.findComponentById("imageAppointmentConfirmed") as Image;
        const isApptRequired = modelRow?.get("stop.appt_required", "N") === "Y";
        const isApptConfirmed = modelRow?.get("stop.confirmed", "N") === "Y";
        const shouldShowConfirmedBadge = (isApptRequired && isApptConfirmed) || isApptConfirmed;
        apptBadgeConfirmed.visible = shouldShowConfirmedBadge;
        apptBadgeRequired.visible = isApptRequired;
    }

    private setLabelStopStatus(stopStatus: any, labelStop: Label, schedArriveEarly: any, schedArriveLate: any) {
        if (stopStatus === STOP_DEPARTED) {
            labelStop.text = "Departed";
        }
        else if (AVAILABLE_STATUS_CODE === stopStatus) {
            if (!StringUtil.isEmptyString(schedArriveEarly) && StringUtil.isEmptyString(schedArriveLate)) {
                labelStop.text = "Arrived";
            } else {
                labelStop.text = "Available";
            }
        }
        else {
            labelStop.text = null;
        }
    }

    /** This is an event handler for the beforeExecution event of sourcePartialShipmentManifestStopDetails. */
    sourcePartialShipmentManifestStopDetailsBeforeExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() === DataSourceAction.SEARCH) {
            this.sourcePartialShipmentManifestStopDetails.mode = DataSourceMode.UPDATE;
            this.notifyStopDetailsChange(false);
        }
    }

    /** This is an event handler for the afterTableRowMove event of tableStopTables. */
    tableStopTablesAfterTableRowMove(event: TableRowMoveEvent) {

        const hasChanged = event.getDataSource().hasChanged();
        if (!hasChanged) {
            this.tableStopsSaveButton.enabled = false;
            this.notifyStopDetailsChange(false);
            return;
        }

        this.tableStopsSaveButton.enabled = true;

        const movedRow: TableRow = event.getTableRow();
        const tableRows: TableRow[] = movedRow.table.rows;
        const movedIndex: number = movedRow.index;
        const movedStatus: string = movedRow.data.get("stop.status");
        const isMoveStopDeparted: boolean = movedStatus === STOP_DEPARTED;
        const rowsAfterMovedRow: TableRow[] = tableRows.slice(movedIndex + 1);
        const hasDepartedStopAfterMovedRow = rowsAfterMovedRow.some(row => row.data.get("stop.status") === STOP_DEPARTED);
        const isUnclearedStopBeforeDepartedStop = !isMoveStopDeparted && hasDepartedStopAfterMovedRow;

        if (isUnclearedStopBeforeDepartedStop) {
            CommonDialogs.showError("An uncleared stop cannot be placed before a departed stop.");
            this.restoreMovedRowPosition(event);
            this.syncStopDetailsChangeState(event.getDataSource());
            return;
        }

        const areNewStopsPositionValid = this.stopsValidated(tableRows);
        if (!areNewStopsPositionValid) {
            this.restoreMovedRowPosition(event);
            this.syncStopDetailsChangeState(event.getDataSource());
            return;
        }

        this.notifyStopDetailsChange(hasChanged);
    }

    private restoreMovedRowPosition(event: TableRowMoveEvent): void {
        const movedRow = event.getTableRow();
        const oldIndex = event.oldIndex ?? event.dragSession?.oldRowIndex;
        if (oldIndex != null) {
            movedRow.table.moveRow(movedRow, oldIndex);
        }
    }

    private syncStopDetailsChangeState(dataSource: DataSource): void {
        const hasUnsavedChanges = dataSource.hasChanged();
        this.tableStopsSaveButton.enabled = hasUnsavedChanges;
        this.notifyStopDetailsChange(hasUnsavedChanges);
    }

    async saveStopChanges(tableStops: Table, dataSource: DataSource) {
        const stops = [];
        tableStops.rows.forEach((row) => {
            if (row.data.hasChanged) {
                const changedData = row.data.getChangedData();
                if (Object.keys(changedData).length > 0) {
                    const stopId = row.data.get("stop.id");
                    const originalMovementSequence = row.data.originalData["stop.movement_sequence"];
                    stops.push({stop_id: stopId, orig_move_seq: originalMovementSequence, ...changedData});
                }
            }
        });
        if (stops.length > 0 && this.stopsValidated(tableStops.rows)) {
            this.tableStopsSaveButton.busy = true;
            try {
                const response = await Api.update("lme/dispatch/partial-shipment-arrange-stops", { stops: stops, movement_id : dataSource.data[0].get("id") });
                const responseData = response?.data?.[0];
                if (!responseData?.success) {
                    CommonDialogs.showError(responseData?.error_message);
                    this.tableStopsSaveButton.enabled = false;
                }
                else {
                    dataSource.search({ id: dataSource.data[0].get("id") });
                    this.tableStopsSaveButton.enabled = false;
                    this.notifyStopDetailsChange(false);
                }
            } finally {
                this.tableStopsSaveButton.busy = false;
            }
            const movementId = dataSource?.lastSearch?.id;
            if (movementId) {
                this.sourcePartialShipmentPlanningManifestProratedRevenue.search({ movement_id: movementId });
            }
        }
    }

    private notifyStopDetailsChange(hasUnsavedChanges: boolean) {
        this.onStopDetailsChange?.(hasUnsavedChanges);
    }

    /** This is an event handler for the afterTabSelection event of tabsetManifestDetails. */
    tabsetManifestDetailsAfterTabSelection(event: SelectionEvent) {
        const tools = (event.target as Tabset).afterTools;
        const rootLayout: Layout = (event.target as Tabset).getRootLayout();
        const layoutName = rootLayout?.layoutName;
        tools.forEach((tool) => {
            if (tool.id === "stopsSaveButton" && event.newSelection?.id === "tabStopDetails"
                && layoutName !== PartialShipmentManifestDetails.MANIFEST_CREATION_LAYOUT) {
                tool.visible = true;
            }
            else if (tool.id === "stopsSaveButton" && event.newSelection?.id !== "tabStopDetails") {
                tool.visible = false;
            }
         });

    }

    private getManifestTotals(): Panel {
        const valueProps = { rowBreak: false, dataSource: this.sourcePartialShipmentPlanningManifestProratedRevenue, fontBold: true, paddingLeft: 0};
        const captionProps = { rowBreak: false, color: "component.palette.textbox.caption.color", paddingRight: 0 };
        const manifestTotalsPanel = new Panel({ id: "panelManifestTotals", fillRow: true, wrap: true });
        const labelTotalWeight = new Label({ id: "labelTotalWeight", text: "Weight:", ...captionProps });
        const labelTotalWeightValue = new Label({ id: "labelTotalWeightValue", text: "Weight:" ,field: "weight", displayType: DisplayType.DECIMAL, ...valueProps });
        const labelTotalPallets = new Label({ id: "labelTotalPallets", text: "Pallets:", ...captionProps });
        const labelTotalPalletsValue = new Label({ id: "labelTotalPalletsValue", field: "pallets", ...valueProps });
        const labelTotalRevenue = new Label({ id: "labelTotalRevenue", text: "Revenue:", ...captionProps });
        const labelTotalRevenueValue = new Label({ id: "labelTotalRevenue", field: "prorated", displayType: DisplayType.CURRENCY, ...valueProps });
        const labelTotalMileage = new Label({ id: "labelTotalMileage", text: "Mileage:", ...captionProps });
        const labelTotalMileageValue = new Label({ id: "labelTotalMileageValue", field: "mileage", displayType: DisplayType.DECIMAL, ...valueProps });
        const labelTotalRevPerDist = new Label({ id: "labelTotalRevPerDist", text: "Rev/Mile:", ...captionProps });
        const labelTotalRevPerDistValue = new Label({ id: "labelTotalWeightValue", field: "revenue_distance", displayType: DisplayType.CURRENCY, ...valueProps });

        manifestTotalsPanel.components = ([labelTotalWeight, labelTotalWeightValue, labelTotalPallets, labelTotalPalletsValue, labelTotalRevenue, labelTotalRevenueValue, labelTotalMileage,
            labelTotalMileageValue, labelTotalRevPerDist, labelTotalRevPerDistValue]);

        return manifestTotalsPanel;
    }

    stopsValidated(rows: TableRow[]): boolean {
        const pickupTypes = PartialShipmentManifestDetails.PICKUP_STOP_TYPES;
        const deliveryTypes = PartialShipmentManifestDetails.DELIVERY_STOP_TYPES;

        const firstStopType: string = rows[0].data.get("stop.stop_type");
        const lastStopType: string = rows[rows.length - 1].data.get("stop.stop_type");

        // Rule 1: Check start and end stop types
        if (!pickupTypes.includes(firstStopType) || !deliveryTypes.includes(lastStopType)) {
            const pickupMessage = "The first stop must be a Pickup or Split Pickup";
            const deliveryMessage = "the last stop must be a Delivery or Split Drop.";
            const message = `${pickupMessage}, and ${deliveryMessage}`;
            CommonDialogs.showError(message);
            return false;
        }

        const allowDeliveryWithoutPickup = firstStopType === SPLIT_PICKUP;
        const orderStopSequences: Map<string, OrderStopSequence> = this.getOrderStopSequences(rows, pickupTypes, deliveryTypes);

        const invalidOrderId = this.getInvalidOrderId(orderStopSequences, allowDeliveryWithoutPickup);
        if (invalidOrderId != null) {
            CommonDialogs.showError(`Pickup must come before delivery for order ${invalidOrderId}.`);
            return false;
        }

        return true;
    }

    createContextMenuItems(tableRow: TableRow): Partial<ContextMenuLabelProps>[] {
        const modelRow = tableRow?.data as ModelRow;
        const orderId = modelRow?.get("stop.order_id");
        const menuItems: Partial<ContextMenuLabelProps>[] = [
            { imageName: ImageName.ysplit, text: "Create Y-Split", onClick: () => this.createYSplit(tableRow)}
        ];
        const removeOrder = { imageName: ImageName.delete, text: `Remove ${orderId} from Manifest`, onClick: () => this.removeOrder(tableRow) };
        const leaveFreightMenuItem = { imageName: ImageName.packageDrop, text: "Leave Freight", onClick: () => this.leaveFreight(tableRow) };
        const allStopRowsFromManifest = tableRow.table.rows as TableRow[];
        const allOrderIDFromManifest = allStopRowsFromManifest.filter(row => row.data.get("stop.order_id") === orderId);
        const allAvailable = allOrderIDFromManifest.length > 0 && allOrderIDFromManifest.every(row => row.data.get("stop.status") === AVAILABLE_STATUS_CODE);
        const hasClearedStop = allOrderIDFromManifest.some(row => row.data.get("stop.status") === STOP_DEPARTED);
        if (allAvailable && !hasClearedStop) {
            menuItems.push(removeOrder);
        }
        if (modelRow?.get("leave_freight_avail") === true) {
            menuItems.push(leaveFreightMenuItem);
        }
        return menuItems;
    }

    createYSplit(tableRow: TableRow): void {
        const companySettings = CompanySettings.get();
        const isAsset = companySettings?.is_asset;
        const isBrokerage = companySettings?.is_brokerage;
        const movementData = tableRow.data;
        const orderId: string = movementData.get("stop.order_id") as string;
        const manifestId: string = this.manifestId as string;
        const movementId: string = this.movementId as string;
        YSplit.showSlideout({
            orderId: orderId,
            isCompanyBoth: isAsset && isBrokerage,
            manifestId: manifestId,
            movementId: movementId,
            doAfterSlideOut: async (success: boolean = false) => {
                if (success) {
                    this.sourcePartialShipmentManifestStopDetails.search({ id: this.movementId });
                    this.sourcePartialShipmentManifestOrderDetails.search({ planned_manifest_id: manifestId });
                    Toast.showSuccessToast("Y-split created successfully");
                }
            }
        });
    }

    async removeOrder(tableRow: TableRow) {
        const orderId = tableRow.data.get("stop.order_id");
        const snackbar = Snackbar.showDownloadSnackbar("Remove Order", "Removing Order " + orderId + " from Manifest", {persist: true});
        const stops = [];
        const stopRows = tableRow.table.rows;
        stopRows.forEach((row, index) => {
            if (orderId === row.data.get("stop.order_id")) {
                row.data.set("stop.movement_sequence", index + 1);
                stops.push({stop_id: row.data.get("stop.id"), movement_id: tableRow.data.get("id"), order_id: orderId});
            }
        });

        if (stops.length > 0) {
            try {
                const response = await Api.update("lme/dispatch/partial-shipment-planning-drop-from-manifest", {stops: stops, manifest_id: this.manifestId});
                const data = response?.data?.[0];
                if (data?.success) {
                    const isDataLeft = data?.should_execute_query;
                    if (isDataLeft) {
                        this.sourcePartialShipmentManifestStopDetails.search({ id: this.movementId });
                        this.sourcePartialShipmentManifestOrderDetails.search({ planned_manifest_id: this.manifestId });
                        this.sourcePartialShipmentPlanningManifestProratedRevenue.search({ movement_id: this.movementId });
                    }
                }
            } catch (e) {
                CommonDialogs.showError(e);
            } finally {
                snackbar.dismiss();
            }
        }
    }

    leaveFreight(tableRow: TableRow) {
        const companySettings = CompanySettings.get();
        const isAsset = companySettings?.is_asset;
        const isBrokerage = companySettings?.is_brokerage;
        const movementData = tableRow.data;
        const orderId: string = movementData.get("stop.order_id") as string;
        const manifestId: string = this.manifestId as string;
        const movementId: string = this.movementId as string;
        const movementSequence: string = movementData.get("stop.movement_sequence") as string;
        YSplit.showSlideout({
            orderId: orderId,
            isCompanyBoth: isAsset && isBrokerage,
            manifestId: manifestId,
            movementId: movementId,
            movementSequence: movementSequence,
            leaveFreight: true,
            doAfterSlideOut: async (success: boolean = false) => {
                if (success) {
                    this.sourcePartialShipmentManifestStopDetails.search({ id: this.movementId });
                    this.sourcePartialShipmentManifestOrderDetails.search({ planned_manifest_id: manifestId });
                    Toast.showSuccessToast("Leave Freight created successfully");
                }
            }
        });
    }

    private getOrderStopSequences(rows: TableRow[], pickupTypes: any, deliveryTypes: any): Map<string, OrderStopSequence> {
        const orderStopSequences = new Map<string, OrderStopSequence>();
        let index = 0;
        for (const row of rows) {
            const orderId = row.data.get("stop.order_id");

            if (!orderId) {
                index++;
                continue;
            }

            const stopType = row.data.get("stop.stop_type");
            const stopSequence = orderStopSequences.get(orderId) ?? {};

            if (pickupTypes.includes(stopType) && stopSequence.pickupIndex === undefined) {
                stopSequence.pickupIndex = index;
            }

            if (deliveryTypes.includes(stopType) && stopSequence.deliveryIndex === undefined) {
                stopSequence.deliveryIndex = index;
            }

            orderStopSequences.set(orderId, stopSequence);
            index++;
        }
        return orderStopSequences;
    }

    private getInvalidOrderId(orderStopSequences: Map<string, OrderStopSequence>, allowDeliveryWithoutPickup: boolean): string | null {
        for (const [orderId, stopSequence] of orderStopSequences.entries()) {
            if (this.isInvalidStopSequence(stopSequence, allowDeliveryWithoutPickup)) {
                return orderId;
            }
        }
        return null;
    }

    private isInvalidStopSequence(stopSequence: OrderStopSequence, allowDeliveryWithoutPickup: boolean): boolean {
        const { pickupIndex, deliveryIndex } = stopSequence;
        const isDeliveryWithoutPickup = deliveryIndex !== undefined && pickupIndex === undefined;
        if (allowDeliveryWithoutPickup && isDeliveryWithoutPickup) {
            return false;
        }

        return deliveryIndex !== undefined && (pickupIndex === undefined || pickupIndex > deliveryIndex);
    }
}
